import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PaginatedResult,
  SupplierActiveFilter,
  SupplierDetail,
  SupplierDuplicateCheckResult,
  SupplierSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';

export interface SupplierListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: SupplierActiveFilter | '';
}

/** Everything a person types about a supplier. The SUP- number is never among it: the server allocates that. */
export interface SupplierInput {
  name: string;
  nameAr?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  taxNumber?: string;
  address?: string;
  paymentTermsDays?: number;
  notes?: string;
}

export const supplierKeys = {
  all: ['suppliers'] as const,
  list: (params: SupplierListParams) => [...supplierKeys.all, 'list', params] as const,
  detail: (id: string) => [...supplierKeys.all, 'detail', id] as const,
};

export function useSuppliersQuery(params: SupplierListParams) {
  return useQuery({
    queryKey: supplierKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<SupplierSummary>>('/suppliers', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.active ? { active: params.active } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useSupplierQuery(id: string | undefined) {
  return useQuery({
    queryKey: supplierKeys.detail(id ?? ''),
    queryFn: () => api.get<SupplierDetail>(`/suppliers/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

function useSupplierInvalidation() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: supplierKeys.all });
}

/**
 * Suppliers that may be the one being entered.
 *
 * A POST rather than a query string, as the API requires: a phone number or a tax number has no
 * business travelling in a URL. Nothing is written, so this is a mutation only in shape.
 */
export function useSupplierDuplicateCheck() {
  return useMutation({
    mutationFn: (payload: {
      name?: string;
      nameAr?: string;
      phone?: string;
      taxNumber?: string;
      excludeSupplierId?: string;
    }) => api.post<SupplierDuplicateCheckResult>('/suppliers/check-duplicates', payload),
  });
}

/** Registering a supplier allocates its number, so a retry must replay the one supplier rather than mint a second. */
export function useCreateSupplierMutation() {
  const invalidate = useSupplierInvalidation();

  return useIdempotentMutation<SupplierDetail, SupplierInput>(
    (payload, config) => api.post<SupplierDetail>('/suppliers', payload, config),
    { onSuccess: invalidate },
  );
}

/** A whole-record save at the version the screen showed. A field left out is cleared, as the API says. */
export function useUpdateSupplierMutation() {
  const invalidate = useSupplierInvalidation();

  return useMutation({
    mutationFn: ({ id, ...payload }: SupplierInput & { id: string; version: number }) =>
      api.put<SupplierDetail>(`/suppliers/${id}`, payload),
    onSuccess: invalidate,
  });
}

export function useSupplierActivationMutation(action: 'deactivate' | 'reactivate') {
  const invalidate = useSupplierInvalidation();

  return useMutation({
    mutationFn: (payload: { id: string; version: number }) =>
      api.post<SupplierDetail>(`/suppliers/${payload.id}/${action}`, {
        version: payload.version,
      }),
    onSuccess: invalidate,
  });
}
